import React, { useState, useEffect, useRef } from 'react';
import userService from '../../../services/userService';
import { fetchEmailTemplates, sendEmail } from '../../api';

const UserApprovalModal = ({ user, requesterEmail, onClose, onApproved, onRejected }) => {
  const [accessLevel, setAccessLevel] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(''); // 'approve' or 'reject'

  const [accessLevels] = useState(userService.getAccessLevels());
  const [availableCountries, setAvailableCountries] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [availableSections] = useState(userService.getAvailableSections());
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  // Use a display user sourced from the same API as UserContext
  const [displayUser, setDisplayUser] = useState(user);
  const [requesterAccess, setRequesterAccess] = useState(null);
  const didPrefillSelections = useRef(false);
  const [isAlreadyApproved, setIsAlreadyApproved] = useState(false);

  // Always fetch latest permissions once per open; seed with passed user immediately
  useEffect(() => {
    let isMounted = true;
    setDisplayUser(user);
    setIsAlreadyApproved(Boolean(user?.status && String(user.status).toLowerCase() === 'approved'));
    const fetchAccess = async () => {
      try {
        if (user?.email) {
          const access = await userService.checkUserAccess(user.email);
          if (isMounted && access && access.user) {
            setDisplayUser(access.user);
            setIsAlreadyApproved(Boolean(access?.isApproved));
          }
        }
      } catch (_) { /* ignore */ }
    };
    fetchAccess();
    return () => { isMounted = false; };
  }, [user]);

  // Set initial access level to first available option
  useEffect(() => {
    if (accessLevels.length > 0 && !accessLevel) {
      setAccessLevel(accessLevels[0].value);
    }
  }, [accessLevels, accessLevel]);

  // If user already has an access level, prefer that
  useEffect(() => {
    if (displayUser?.accessLevel && accessLevels.some(l => l.value === displayUser.accessLevel)) {
      setAccessLevel(displayUser.accessLevel);
    }
  }, [displayUser?.accessLevel, accessLevels]);

  // Load countries and regions on component mount
  useEffect(() => {
    const loadCountriesAndRegions = async () => {
      try {
        setCountriesLoading(true);
        const [countriesData, regionsData] = await Promise.all([
          userService.getAvailableCountries(),
          userService.getRegions()
        ]);
        // Map countries to expected structure: { value, label, region }
        const mappedCountries = countriesData.map(c => ({
          value: String(c.iso3 || '').toUpperCase(),
          label: c.Country_Name,
          region: c.UNICEF_RO || c.WHO_RO || c.Gavi_RO || ''
        }));
        setAllCountries(mappedCountries);
        // Apply initial filter if requesterAccess is already known
        if (requesterAccess?.user?.accessLevel === 'country-admin') {
          const allowedIsoSet = new Set(
            (Array.isArray(requesterAccess.user.allowedCountriesIso3)
              ? requesterAccess.user.allowedCountriesIso3
              : [])
              .map(v => String(v).toUpperCase())
          );
          const filtered = mappedCountries.filter(c => allowedIsoSet.has(c.value));
          setAvailableCountries(filtered);
        } else {
          setAvailableCountries(mappedCountries);
        }
        // Map regions (string array from API) to expected structure: { value, label }
        setRegions(
          (regionsData || [])
            .filter(r => typeof r === 'string' && r.trim())
            .map(r => ({ value: r, label: r }))
        );
      } catch (error) {
        console.error('Error loading countries and regions:', error);
      } finally {
        setCountriesLoading(false);
      }
    };

    loadCountriesAndRegions();
  }, [requesterAccess]);

  // Load requester access when requesterEmail is provided
  useEffect(() => {
    let isMounted = true;
    const loadRequesterAccess = async () => {
      try {
        if (requesterEmail) {
          const access = await userService.checkUserAccess(requesterEmail);
          if (isMounted && access && access.user) {
            setRequesterAccess(access);
          }
        }
      } catch (_) { /* ignore */ }
    };
    loadRequesterAccess();
    return () => { isMounted = false; };
  }, [requesterEmail]);

  // When requesterAccess or allCountries changes, enforce country-admin scoping on countries list
  useEffect(() => {
    if (!Array.isArray(allCountries) || allCountries.length === 0) return;
    if (requesterAccess?.user?.accessLevel === 'country-admin') {
      const allowedIsoSet = new Set(
        (Array.isArray(requesterAccess.user.allowedCountriesIso3)
          ? requesterAccess.user.allowedCountriesIso3
          : [])
          .map(v => String(v).toUpperCase())
      );
      const filtered = allCountries.filter(c => allowedIsoSet.has(c.value));
      setAvailableCountries(filtered);
      // Trim selected countries to allowed set
      setSelectedCountries(prev => (Array.isArray(prev) ? prev.filter(v => allowedIsoSet.has(String(v).toUpperCase())) : []));
    } else if (availableCountries.length === 0) {
      setAvailableCountries(allCountries);
    }
  }, [requesterAccess, allCountries, availableCountries.length]);

  // Prefill countries and sections for already approved users once options are loaded
  useEffect(() => {
    if (didPrefillSelections.current) return;
    if (countriesLoading) return;

    const iso3FromUser = Array.isArray(displayUser?.allowedCountriesIso3)
      ? displayUser.allowedCountriesIso3
      : [];

    let initialCountries = [];
    if (iso3FromUser.length > 0) {
      initialCountries = iso3FromUser.map(v => String(v).toUpperCase());
    } else if (Array.isArray(displayUser?.allowedCountries) && displayUser.allowedCountries.length > 0) {
      initialCountries = displayUser.allowedCountries.map(name => {
        const match = availableCountries.find(c => String(c.label).toLowerCase() === String(name).toLowerCase());
        return match?.value;
      }).filter(Boolean);
    }

    const userSections = Array.isArray(displayUser?.allowedSections)
      ? displayUser.allowedSections
      : (typeof displayUser?.allowedSections === 'string' && displayUser.allowedSections.trim().startsWith('['))
        ? (() => { try { return JSON.parse(displayUser.allowedSections); } catch { return []; } })()
        : [];

    const filteredSections = userSections.filter(s => availableSections.some(a => a.value === s));

    if (initialCountries.length > 0 || filteredSections.length > 0) {
      setSelectedCountries(initialCountries);
      setSelectedSections(filteredSections);
      didPrefillSelections.current = true;
    }
  }, [countriesLoading, availableCountries, availableSections, displayUser?.allowedCountriesIso3, displayUser?.allowedCountries, displayUser?.allowedSections]);

  const handleApprove = async () => {
    setLoading(true);
    setAction('approve');
    
    try {
      if (isAlreadyApproved || String(displayUser?.status || '').toLowerCase() === 'approved') {
        await userService.updateUserPermissions(
          user.id,
          accessLevel,
          selectedCountries,
          selectedSections,
          requesterEmail || 'thrive360@unicef.org'
        );
      } else {
        await userService.approveUser(
          user.id,
          requesterEmail || 'thrive360@unicef.org', 
          accessLevel,
          selectedCountries,
          selectedSections
        );
      }
      // Send approval email notification (non-blocking for UI reliability)
      if (!(isAlreadyApproved || String(displayUser?.status || '').toLowerCase() === 'approved')) {
        try {
          const templates = await fetchEmailTemplates();
          const tpl = templates?.account_approval || {};

          const fullName = [user.name, user.lastName].filter(Boolean).join(' ').trim() || user.email;
          const loginUrl = `https://thrive360.unicef.org/stock-performance`;

          const interpolate = (input = '') =>
            String(input)
              .replace(/{{\s*name\s*}}/gi, fullName)
              .replace(/{{\s*loginUrl\s*}}/gi, loginUrl);

          const subject = interpolate(tpl.subject || 'Your Thrive360 account is now active');
          const html = interpolate(typeof tpl === 'string' ? tpl : tpl?.html || '');

          if (user?.email) {
            await sendEmail({
              to: user.email,
              from: 'THRIVE360 | UNICEF <thrive360@mail.unicef.org>',
              subject,
              html
            });
          }
        } catch (mailErr) {
          console.error('Failed to send approval email:', mailErr);
        }
      }
      
      onApproved(user.id);
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    setLoading(true);
    setAction('reject');
    
    try {
      await userService.rejectUser(
        user.id,
        requesterEmail || 'thrive360@unicef.org',
        rejectionReason
      );
      
      onRejected(user.id);
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Failed to reject user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryToggle = (countryValue) => {
    setSelectedCountries(prev => 
      prev.includes(countryValue)
        ? prev.filter(c => c !== countryValue)
        : [...prev, countryValue]
    );
  };

  const handleSectionToggle = (sectionValue) => {
    setSelectedSections(prev => 
      prev.includes(sectionValue)
        ? prev.filter(s => s !== sectionValue)
        : [...prev, sectionValue]
    );
  };

  const handleSelectAllCountries = () => {
    setSelectedCountries(availableCountries.map(c => c.value));
  };

  const handleSelectRegion = async (regionName) => {
    try {
      const regionCountries = await userService.getCountriesByRegion(regionName);
      const isAlreadySelected = selectedRegions.includes(regionName);

      if (isAlreadySelected) {
        // Deselect: remove all countries from this region
        const regionIsoSet = new Set(
          regionCountries
            .map(c => String(c?.value || c?.iso3 || c?.ISO3 || '').toUpperCase())
            .filter(Boolean)
        );
        setSelectedCountries(prev => prev.filter(c => !regionIsoSet.has(String(c).toUpperCase())));
        setSelectedRegions(prev => prev.filter(r => r !== regionName));
      } else {
        // Select: add all countries from this region
        const newSelection = new Set(
          (Array.isArray(selectedCountries) ? selectedCountries : []).map(v => String(v).toUpperCase())
        );
        regionCountries.forEach(country => {
          const iso3 = country?.value || country?.iso3 || country?.ISO3 || '';
          if (iso3) newSelection.add(String(iso3).toUpperCase());
        });
        setSelectedCountries(Array.from(newSelection));
        setSelectedRegions(prev => [...prev, regionName]);
      }
    } catch (error) {
      console.error('Error selecting region countries:', error);
    }
  };

  const handleSelectAllSections = () => {
    setSelectedSections(availableSections.map(s => s.value));
  };

  const handleClearAllCountries = () => {
    setSelectedCountries([]);
    setSelectedRegions([]);
  };

  const handleClearAllSections = () => {
    setSelectedSections([]);
  };

  // Filter out Admin Panel from availableSections
  const adminPanelSection = availableSections.find(s => s.value === 'admin');
  const filteredSections = availableSections.filter(s => s.value !== 'admin');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {action === 'approve' ? 'Approve User' : action === 'reject' ? 'Reject User' : 'Review User'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
          </div>

          {/* User Information */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium text-gray-700 mb-3">User Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{user.name} {user.lastName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Country</p>
                <p className="font-medium">{user.country || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Registration Date</p>
                <p className="font-medium">{new Date(user.createdAt).toLocaleDateString()}</p>
              </div>
              
            </div>
          </div>

          {/* Approval Form */}
          <div className="space-y-6">
            {/* Access Level */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Access Level</h4>
            <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
              {(requesterAccess?.user?.accessLevel === 'country-admin'
                ? accessLevels.filter(level => level.value === 'country' || level.value === 'country-admin')
                : accessLevels
              ).map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label} - {level.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Countries Access */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700">Countries Access</h4>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={handleSelectAllCountries}
                    className="text-sm text-blue-600 hover:text-blue-800"
                    disabled={loading}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllCountries}
                    className="text-sm text-red-600 hover:text-red-800"
                    disabled={loading}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              {/* Quick Select UNICEF Regions Only */}
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2">Quick Select UNICEF Regions:</p>
                <div className="flex flex-wrap gap-2">
                  {regions
                    .filter(region => region.value && region.value !== 'Global')
                    .map(region => (
                      <button
                        key={region.value}
                        type="button"
                        onClick={() => handleSelectRegion(region.value)}
                        className={`${selectedRegions.includes(region.value) ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border'} px-3 py-1 text-xs rounded-full`}
                        disabled={loading}
                      >
                        {region.label}
                      </button>
                    ))}
                </div>
              </div>
              
              {countriesLoading ? (
                <div className="flex items-center justify-center p-4">
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  <span>Loading countries...</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {availableCountries.map(country => (
                    <label key={country.value} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedCountries.includes(country.value)}
                        onChange={() => handleCountryToggle(country.value)}
                        disabled={loading}
                        className="rounded"
                      />
                      <span>{country.label}</span>
                      {country.region && country.region !== 'Global' && (
                        <span className="text-xs text-gray-400">({country.region})</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Sections Access */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700">Sections Access</h4>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={handleSelectAllSections}
                    className="text-sm text-blue-600 hover:text-blue-800"
                    disabled={loading}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllSections}
                    className="text-sm text-red-600 hover:text-red-800"
                    disabled={loading}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                {filteredSections.map(section => (
                  <label key={section.value} className="flex items-start space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(section.value)}
                      onChange={() => handleSectionToggle(section.value)}
                      disabled={loading}
                      className="rounded mt-1"
                    />
                    <div>
                      <span className="font-medium">{section.label}</span>
                      <p className="text-xs text-gray-500">{section.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              {/* Admin Panel as separate item */}
              {adminPanelSection && (
                <div className="mt-4 border-t pt-4">
                  <label className="flex items-start space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(adminPanelSection.value)}
                      onChange={() => handleSectionToggle(adminPanelSection.value)}
                      disabled={loading}
                      className="rounded mt-1"
                    />
                    <div>
                      <span className="font-medium text-red-700">{adminPanelSection.label}</span>
                      <p className="text-xs text-gray-500">{adminPanelSection.description}</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Rejection Reason */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Rejection Reason (if rejecting)</h4>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                disabled={loading}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={loading || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            >
              {loading && action === 'reject' ? (
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
              ) : (
                <i className="fa-solid fa-times mr-2"></i>
              )}
              Reject User
            </button>
            <button
              onClick={handleApprove}
              disabled={loading || selectedCountries.length === 0 || selectedSections.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            >
              {loading && action === 'approve' ? (
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
              ) : (
                <i className="fa-solid fa-check mr-2"></i>
              )}
              {isAlreadyApproved || String(displayUser?.status || '').toLowerCase() === 'approved' ? 'Update Permissions' : 'Approve User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserApprovalModal; 